import React, { useState, useRef, useEffect } from 'react';
import { LeftOutlined, RightOutlined, DownOutlined, CheckCircleFilled, ClockCircleOutlined } from '@ant-design/icons';

const AssessmentNavBar = ({
  assessmentList = [],
  currentIndex = 0,
  onNavigate,
  loading = false
}) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  if (!assessmentList || assessmentList.length <= 1) return null;

  const isItemVisible = (item) => {
    if (item.isDiagnosis) return true;
    // Show every assessment that has STARTED — ongoing, completed, AND expired
    // ("Expired - Completed" / "Expired - Lapsed"). Only hide "Upcoming" runs
    // that haven't started yet (nothing to view). Previously this allowed only
    // completed/ongoing, which hid expired schedule runs from the nav.
    const status = (item.status || '').toLowerCase();
    if (status === 'upcoming') return false;
    return true;
  };

  const visibleItems = assessmentList
    .map((item, idx) => ({ item, originalIndex: idx }))
    .filter(({ item }) => isItemVisible(item));

  if (visibleItems.length <= 1) return null;

  const currentVisiblePos = visibleItems.findIndex(v => v.originalIndex === currentIndex);
  const isFirst = currentVisiblePos <= 0;
  const isLast = currentVisiblePos >= visibleItems.length - 1;

  const handlePrev = () => {
    if (currentVisiblePos > 0 && onNavigate) {
      onNavigate(visibleItems[currentVisiblePos - 1].originalIndex);
    }
  };

  const handleNext = () => {
    if (currentVisiblePos < visibleItems.length - 1 && onNavigate) {
      onNavigate(visibleItems[currentVisiblePos + 1].originalIndex);
    }
  };

  const getStatusIcon = (item) => {
    if (item.isCompleted || (item.status || '').toLowerCase() === 'completed') {
      return <CheckCircleFilled style={{ color: '#52c41a', fontSize: 13 }} />;
    }
    if (item.isOngoing || (item.status || '').toLowerCase() === 'ongoing') {
      return <ClockCircleOutlined style={{ color: '#3249D7', fontSize: 13 }} />;
    }
    return <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', border: '2px solid #d9d9d9', verticalAlign: 'middle' }} />;
  };

  const getLabel = (item, attemptIdx) => {
    if (item.isDiagnosis) return 'Diagnosis';
    return `Assessment ${attemptIdx}`;
  };

  let currentLabel = 'Select';
  const currentVisible = visibleItems.find(v => v.originalIndex === currentIndex);
  if (currentVisible) {
    let aIdx = 0;
    for (let i = 0; i <= currentVisible.originalIndex; i++) {
      if (!assessmentList[i].isDiagnosis) aIdx++;
    }
    currentLabel = getLabel(currentVisible.item, aIdx);
  }

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8,
      padding: '8px 12px', background: '#fff', borderRadius: 10,
      marginBottom: 12, border: '1px solid #e8e8e8',
      boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
    }}>
      <button
        disabled={isFirst || loading}
        onClick={handlePrev}
        style={{
          width: 30, height: 30, borderRadius: 8, border: '1px solid #e8e8e8',
          background: isFirst ? '#f5f5f5' : '#fff', cursor: isFirst ? 'not-allowed' : 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: isFirst ? '#bfbfbf' : '#595959', transition: 'all 0.2s',
        }}
      >
        <LeftOutlined style={{ fontSize: 12 }} />
      </button>

      {/* Custom dropdown */}
      <div ref={wrapperRef} style={{ position: 'relative', flex: 1, minWidth: 180 }}>
        <button
          onClick={() => !loading && setOpen(!open)}
          style={{
            width: '100%', height: 32, borderRadius: 8,
            border: '1px solid #d9d9d9', background: '#fff',
            cursor: loading ? 'not-allowed' : 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '0 12px', fontSize: 13, fontWeight: 500,
            color: '#262626',
          }}
        >
          <span>{currentLabel}</span>
          <DownOutlined style={{ fontSize: 10, color: '#8c8c8c', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
        </button>

        {open && (
          <div style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            marginTop: 4,
            background: '#fff',
            borderRadius: 10,
            border: '1px solid #e8e8e8',
            boxShadow: '0 6px 16px rgba(0,0,0,0.12)',
            zIndex: 1050,
            maxHeight: 220,
            overflowY: 'auto',
            padding: 4,
          }}>
            {visibleItems.map(({ item, originalIndex }) => {
              let attemptIdx = 0;
              for (let i = 0; i <= originalIndex; i++) {
                if (!assessmentList[i].isDiagnosis) attemptIdx++;
              }
              const isActive = originalIndex === currentIndex;
              return (
                <div
                  key={originalIndex}
                  onClick={() => {
                    if (onNavigate) onNavigate(originalIndex);
                    setOpen(false);
                  }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '8px 12px', borderRadius: 6, cursor: 'pointer',
                    background: isActive ? '#f0f3ff' : 'transparent',
                    transition: 'background 0.15s',
                  }}
                  onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.background = '#f5f5f5'; }}
                  onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
                >
                  {getStatusIcon(item)}
                  <span style={{
                    fontWeight: isActive ? 600 : 400,
                    color: isActive ? '#3249D7' : '#262626',
                    fontSize: 13,
                  }}>
                    {getLabel(item, attemptIdx)}
                  </span>
                  {(item.startDate || item.startTime) && (
                    <span style={{ color: '#8c8c8c', fontSize: 11, marginLeft: 'auto' }}>
                      {new Date(item.startDate || item.startTime).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: '2-digit', timeZone: 'UTC' })}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <button
        disabled={isLast || loading}
        onClick={handleNext}
        style={{
          width: 30, height: 30, borderRadius: 8, border: '1px solid #e8e8e8',
          background: isLast ? '#f5f5f5' : '#fff', cursor: isLast ? 'not-allowed' : 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: isLast ? '#bfbfbf' : '#595959', transition: 'all 0.2s',
        }}
      >
        <RightOutlined style={{ fontSize: 12 }} />
      </button>

      <span style={{
        fontSize: 11, fontWeight: 500, color: '#8c8c8c',
        background: '#f5f5f5', padding: '3px 8px', borderRadius: 6,
        whiteSpace: 'nowrap',
      }}>
        {(currentVisiblePos >= 0 ? currentVisiblePos + 1 : 1)}/{visibleItems.length}
      </span>
    </div>
  );
};

export default AssessmentNavBar;
